import { useState } from 'react'
import type { JSX } from 'react'

import { Button, Card, IconButton, Text, XIcon } from '@buffer-mono/popcorn'

import { useReloadOnNavigation } from '~publish/hooks/useReloadOnNavigation'
import { useSessionExpired } from '~publish/hooks/useSessionExpired'
import { useUpdateAvailable } from '~publish/hooks/useUpdateAvailable'

import styles from './UpdateAvailableBanner.module.css'
import { useStaleAppReloadTracking } from './useStaleAppReloadTracking'

interface UpdateAvailableBannerProps {
  /**
   * Reloads the page to pick up the new version. Injectable because jsdom
   * does not allow stubbing `window.location.reload` in tests.
   */
  onReload?: () => void
}

/**
 * Floating notice shown at the bottom-left of the screen (mirroring the
 * changelog notification) when a newer version of the app has been deployed.
 */
export function UpdateAvailableBanner({
  onReload = (): void => window.location.reload(),
}: UpdateAvailableBannerProps): JSX.Element | null {
  const { updateAvailable } = useUpdateAvailable()
  const sessionExpired = useSessionExpired()
  const { trackStaleAppReloaded } = useStaleAppReloadTracking()
  const [dismissed, setDismissed] = useState(false)

  // Refresh at the next route change — even if the notice was dismissed — so
  // stale sessions pick up the new bundle at a natural navigation boundary.
  // Triggered by a detected update, or by session age alone as a backstop.
  useReloadOnNavigation(updateAvailable || sessionExpired, (): void => {
    trackStaleAppReloaded(
      updateAvailable ? 'updateNavigation' : 'sessionAgeNavigation',
    )
    onReload()
  })

  if (!updateAvailable || dismissed) return null

  return (
    <output className={styles.wrapper}>
      <Card padding="none" className={styles.card}>
        <span className={styles.iconTile} aria-hidden="true">
          🚀
        </span>

        <Text weight="semibold" className={styles.heading}>
          A new version of Buffer is available
        </Text>

        <Button
          variant="primary"
          className={styles.reloadButton}
          onClick={(): void => {
            trackStaleAppReloaded('noticeClick')
            onReload()
          }}
        >
          Reload to Update
        </Button>

        <IconButton
          variant="tertiary"
          size="small"
          label="Dismiss"
          onClick={(): void => setDismissed(true)}
          className={styles.closeButton}
        >
          <XIcon />
        </IconButton>
      </Card>
    </output>
  )
}
